import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { Search, ChevronDown, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Checkbox } from './ui/checkbox';
import { InfoTip } from './InfoTip';
import { FIELD_INFO } from '../lib/fieldInfo';
import * as SliderPrimitive from '@radix-ui/react-slider';

export const NON_OLTA = 'non_olta';
export const SECTOR_OPTIONS = [
  { v: 'Energy', l: 'Energy' }, { v: 'Utilities', l: 'Utilities' }, { v: 'Transportation', l: 'Transportation' },
  { v: 'Digital', l: 'Digital' }, { v: 'Social/Public', l: 'Social/Public' }, { v: NON_OLTA, l: 'non-OLTA Sectors' },
];
export const RECURRING_OPTIONS = [{ v: 'strong', l: 'Strong' }, { v: 'medium', l: 'Medium' }, { v: 'weak', l: 'Weak' }];
export const SUCCESSION_OPTIONS = [{ v: 'yes', l: 'Yes' }, { v: 'maybe', l: 'Maybe' }, { v: 'no', l: 'No' }];
export const INFRA_OPTIONS = [{ v: 'fit', l: 'Fit' }, { v: 'fringe', l: 'Fringe' }, { v: 'no_fit', l: 'No Fit' }];
export const RATING_OPTIONS = [{ v: 'heuristic', l: 'Heuristik (auto)' }, { v: 'ai', l: 'KI / Claude' }];
export const SOURCE_OPTIONS = [{ v: 'proprietary', l: 'Proprietary' }, { v: 'brokered', l: 'Brokered' }];
export const NO_CATEGORY = '__none__';
// Import categories (Talu, 22.10.2026) — shared cache so filterSummary can label chips without a hook
let _categoryCache = null;
let _categoryPromise = null;
export function loadImportCategories() {
  if (_categoryCache) return Promise.resolve(_categoryCache);
  if (!_categoryPromise) {
    _categoryPromise = api.listImportCategories()
      .then(res => { _categoryCache = res.data.categories || []; return _categoryCache; })
      .catch(() => { _categoryPromise = null; return []; });
  }
  return _categoryPromise;
}
export const invalidateImportCategories = () => { _categoryCache = null; _categoryPromise = null; };
export const categoryOptionsFrom = (cats) => [
  ...(cats || []).map(c => ({ v: c.id, l: c.archived ? `${c.name} (archiviert)` : c.name })),
  { v: NO_CATEGORY, l: 'Ohne Kategorie' },
];
export function useImportCategories() {
  const [cats, setCats] = useState(_categoryCache || []);
  useEffect(() => { loadImportCategories().then(setCats); }, []);
  return cats;
}

export const STAGE_OPTIONS = [
  { v: 'orbis_total', l: 'Orbis Total' },
  { v: 'succession_possible', l: 'Succession Possible' },
  { v: 'ai_reviewed', l: 'OLTA TAM (AI Reviewed)' },
  { v: 'olta_reviewed', l: 'Under OLTA Review' },
  { v: 'olta_approved', l: 'OLTA Approved' },
  { v: 'outreach_wip', l: 'Outreach WIP' },
  { v: 'contacted', l: 'Contacted' },
  { v: 'passed', l: 'Passed' },
  { v: 'passed_benched', l: 'Passed (Benched)' },
];

export const buildExploreBody = (f) => {
  const arr = (v) => (Array.isArray(v) && v.length ? v : undefined);
  return {
    keyword: f.keyword && f.keyword.length >= 2 ? f.keyword : undefined,
    include_keywords: arr(f.include_keywords),
    include_mode: (f.include_keywords || []).length ? (f.include_mode || 'any') : undefined,
    exclude_keywords: arr(f.exclude_keywords),
    sector: arr(f.sector),
    subsector: f.subsector || undefined,
    recurring_revenue: arr(f.recurring_revenue),
    succession_case: arr(f.succession_case),
    infrastructure_adjacent: arr(f.infrastructure_adjacent),
    rating_source: arr(f.rating_source),
    deal_type: arr(f.deal_type),
    pipeline_stage: arr(f.pipeline_stage),
    import_category: arr(f.import_category),
    owner_id: arr(f.owner_id),
    exclude_claimed: f.exclude_claimed || undefined,
    ac_flagged: f.ac_flagged || undefined,
    ac_feedback: f.ac_feedback || undefined,
    founded_min: f.founded_min || undefined,
    founded_max: f.founded_max || undefined,
    employees_min: f.employees_min || undefined,
    employees_max: f.employees_max || undefined,
    revenue_min: f.revenue_min || undefined,
    revenue_max: f.revenue_max || undefined,
    rpe_min: f.rpe_min || undefined,
    rpe_max: f.rpe_max || undefined,
    owner_age_min: f.owner_age_min || undefined,
    owner_age_max: f.owner_age_max || undefined,
    succession_score_min: f.succession_score_min || undefined,
    succession_score_max: f.succession_score_max || undefined,
    exclude_viewed: f.exclude_viewed || undefined,
    exclude_pass_proposed: f.exclude_pass_proposed || undefined,
    asset_heavy_min: f.asset_heavy_min || undefined,
    asset_heavy_max: f.asset_heavy_max || undefined,
  };
};

export const DEFAULT_FILTERS = {
  keyword: '', sector: [], subsector: '', recurring_revenue: [], succession_case: [], infrastructure_adjacent: [],
  include_keywords: [], include_mode: 'any', exclude_keywords: [],
  rating_source: [],
  deal_type: [], pipeline_stage: [], import_category: [], owner_id: [], exclude_claimed: false, ac_flagged: false, ac_feedback: '',
  founded_min: '', founded_max: '', employees_min: '', employees_max: '',
  revenue_min: '', revenue_max: '', owner_age_min: '', owner_age_max: '',
  rpe_min: '', rpe_max: '',
  succession_score_min: '', succession_score_max: '',
  exclude_viewed: false, exclude_pass_proposed: false,
  asset_heavy_min: '', asset_heavy_max: '',
  sort: [{ field: 'name', dir: 'asc' }],
};

const OPT_LABEL = (opts, v) => (opts.find(o => o.v === v)?.l || v);
const RANGE_LABELS = {
  founded: ['Gegründet', ''], employees: ['MA', ''], revenue: ['Umsatz', ' €m'], rpe: ['Umsatz/MA', ' €k'],
  owner_age: ['Inhaber-Alter', ''], succession_score: ['Succession', ''], asset_heavy: ['Asset-Heavy', ''],
};

/** Human-readable chips for the active filters (batch header, similar-results header, badge count). */
export function filterSummary(f, ownerOptions = []) {
  if (!f) return [];
  const out = [];
  const list = (v) => (Array.isArray(v) ? v : v ? [v] : []);
  if (f.keyword) out.push(`„${f.keyword}“`);
  if (list(f.include_keywords).length) out.push(`enthält ${list(f.include_keywords).join(f.include_mode === 'all' ? ' + ' : ' / ')}`);
  if (list(f.exclude_keywords).length) out.push(`ohne ${list(f.exclude_keywords).join(', ')}`);
  if (list(f.sector).length) out.push(`Sector: ${list(f.sector).map(v => OPT_LABEL(SECTOR_OPTIONS, v)).join(', ')}`);
  if (f.subsector) out.push(`Subsector: ${f.subsector}`);
  if (list(f.recurring_revenue).length) out.push(`Recurring: ${list(f.recurring_revenue).map(v => OPT_LABEL(RECURRING_OPTIONS, v)).join(', ')}`);
  if (list(f.succession_case).length) out.push(`Succession Case: ${list(f.succession_case).map(v => OPT_LABEL(SUCCESSION_OPTIONS, v)).join(', ')}`);
  if (list(f.infrastructure_adjacent).length) out.push(`Infra: ${list(f.infrastructure_adjacent).map(v => OPT_LABEL(INFRA_OPTIONS, v)).join(', ')}`);
  if (list(f.rating_source).length) out.push(`Bewertung: ${list(f.rating_source).map(v => OPT_LABEL(RATING_OPTIONS, v)).join(', ')}`);
  if (list(f.deal_type).length) out.push(`Source: ${list(f.deal_type).map(v => OPT_LABEL(SOURCE_OPTIONS, v)).join(', ')}`);
  if (list(f.pipeline_stage).length) out.push(`Stage: ${list(f.pipeline_stage).map(v => OPT_LABEL(STAGE_OPTIONS, v)).join(', ')}`);
  if (list(f.import_category).length) out.push(`Kategorie: ${list(f.import_category).map(v => OPT_LABEL(categoryOptionsFrom(_categoryCache), v)).join(', ')}`);
  if (list(f.owner_id).length) out.push(`Claimed by: ${list(f.owner_id).map(v => OPT_LABEL(ownerOptions, v)).join(', ')}`);
  if (f.exclude_claimed) out.push('nur unclaimed');
  if (f.exclude_viewed) out.push('ohne gesehene');
  if (f.exclude_pass_proposed) out.push('ohne Pass-Vorschläge');
  if (f.ac_flagged) out.push('AC „!"');
  if (f.ac_feedback) out.push(`AC-Feedback: ${f.ac_feedback}`);
  Object.entries(RANGE_LABELS).forEach(([k, [label, unit]]) => {
    const lo = f[`${k}_min`], hi = f[`${k}_max`];
    if (lo && hi) out.push(`${label} ${lo}–${hi}${unit}`);
    else if (lo) out.push(`${label} ≥ ${lo}${unit}`);
    else if (hi) out.push(`${label} ≤ ${hi}${unit}`);
  });
  return out;
}

/** Sector config + claim owners — the two option lists the panel needs from the server. */
export function useFilterOptions() {
  const [sectorConfig, setSectorConfig] = useState({});
  const [ownerOptions, setOwnerOptions] = useState([{ v: '__unclaimed__', l: 'Unclaimed' }]);
  useEffect(() => {
    api.getSectorConfig().then(res => setSectorConfig(res.data.sectors || {})).catch(() => {});
    api.listTeamMembers().then(res => {
      const members = (res.data || []).filter(m => m.role !== 'ac_member');
      setOwnerOptions([{ v: '__unclaimed__', l: 'Unclaimed' }, ...members.map(m => ({ v: m.id, l: m.name }))]);
    }).catch(() => {});
  }, []);
  return { sectorConfig, ownerOptions };
}

// ─── Multi-select (checkbox) filter ──────────────────────────
export function MultiSelect({ testid, label, info, options, selected, onChange, placeholder = 'All' }) {
  const toggle = (v) => {
    if (selected.includes(v)) onChange(selected.filter(x => x !== v));
    else onChange([...selected, v]);
  };
  const summary = selected.length === 0
    ? placeholder
    : selected.length === 1
      ? (options.find(o => o.v === selected[0])?.l || selected[0])
      : `${selected.length} selected`;
  return (
    <div>
      <p className="text-[10px] text-muted-foreground mb-1 flex items-center gap-1">{label}{info && <InfoTip text={info} />}</p>
      <Popover>
        <PopoverTrigger asChild>
          <button data-testid={`filter-${testid}`}
            className={`w-full flex items-center justify-between gap-1 bg-secondary border border-border rounded-sm h-8 px-2.5 text-[11px] ${selected.length ? 'text-foreground' : 'text-muted-foreground'} hover:border-muted-foreground/40 transition-colors`}>
            <span className="truncate">{summary}</span>
            <ChevronDown className="w-3 h-3 shrink-0 opacity-60" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-52 p-1 bg-card border-border rounded-sm">
          {selected.length > 0 && (
            <button onClick={() => onChange([])} data-testid={`filter-${testid}-clear`}
              className="w-full text-left text-[10px] text-muted-foreground hover:text-foreground px-2 py-1 mb-0.5 border-b border-border/60">
              Clear ({selected.length})
            </button>
          )}
          <div className="max-h-64 overflow-y-auto custom-scrollbar">
            {options.map(o => (
              <label key={o.v} data-testid={`filter-${testid}-opt-${o.v}`}
                className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-secondary rounded-sm">
                <Checkbox checked={selected.includes(o.v)} onCheckedChange={() => toggle(o.v)} className="w-3.5 h-3.5" />
                <span className="text-xs text-foreground">{o.l}</span>
              </label>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

// ─── Succession-score dual-thumb range slider ───────────────
export function ScoreRange({ min, max, onChange }) {
  const lo = min === '' || min == null ? 0 : Number(min);
  const hi = max === '' || max == null ? 100 : Number(max);
  return (
    <div>
      <p className="text-[10px] text-muted-foreground mb-1 flex items-center gap-1">Succession Score<InfoTip text={FIELD_INFO.succession_score} /></p>
      <div className="flex items-center gap-2 h-7" data-testid="score-range">
        <span className="text-[11px] font-mono w-6 text-right text-foreground/70">{lo}</span>
        <SliderPrimitive.Root
          className="relative flex-1 flex items-center h-5 touch-none select-none cursor-pointer"
          min={0} max={100} step={5} value={[lo, hi]} minStepsBetweenThumbs={0}
          onValueChange={([a, b]) => onChange(a <= 0 ? '' : a, b >= 100 ? '' : b)}>
          <SliderPrimitive.Track className="relative h-1.5 w-full grow overflow-hidden rounded-full bg-primary/15">
            <SliderPrimitive.Range className="absolute h-full bg-blue-500" />
          </SliderPrimitive.Track>
          <SliderPrimitive.Thumb data-testid="score-thumb-min" className="block h-3.5 w-3.5 rounded-full border border-blue-400 bg-background shadow focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-400" />
          <SliderPrimitive.Thumb data-testid="score-thumb-max" className="block h-3.5 w-3.5 rounded-full border border-blue-400 bg-background shadow focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-400" />
        </SliderPrimitive.Root>
        <span className="text-[11px] font-mono w-6 text-foreground/70">{hi}</span>
      </div>
    </div>
  );
}

// ─── Keyword chip input (include / exclude rules) ───────────
export function KeywordChips({ label, chips, onChange, color, placeholder, testId, extra }) {
  const [val, setVal] = useState('');
  const add = () => {
    const parts = val.split(',').map(s => s.trim()).filter(Boolean);
    if (!parts.length) { setVal(''); return; }
    const next = [...chips];
    parts.forEach(p => { if (!next.some(c => c.toLowerCase() === p.toLowerCase())) next.push(p); });
    onChange(next.slice(0, 20));
    setVal('');
  };
  return (
    <div className="flex-1 min-w-[260px]">
      <p className="text-[10px] text-muted-foreground mb-1 flex items-center gap-1.5">{label}{extra}</p>
      <div className={`flex flex-wrap items-center gap-1 border rounded-sm px-1.5 py-1 bg-secondary ${color.border}`}>
        {chips.map(c => (
          <span key={c} className={`flex items-center gap-1 text-[11px] rounded px-1.5 py-0.5 ${color.chip}`} data-testid={`${testId}-chip`}>
            {c}
            <button onClick={() => onChange(chips.filter(x => x !== c))} className="opacity-70 hover:opacity-100" data-testid={`${testId}-remove-${c}`}>
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input value={val} onChange={e => setVal(e.target.value)} data-testid={`${testId}-input`}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(); } }}
          onBlur={add}
          placeholder={chips.length ? '' : placeholder}
          className="flex-1 min-w-[110px] bg-transparent text-xs text-foreground outline-none h-6 px-1 placeholder:text-muted-foreground/50" />
      </div>
    </div>
  );
}

const RangeInputs = ({ label, info, lo, hi, onLo, onHi, phLo = 'Min', phHi = 'Max', testId }) => (
  <div>
    <p className="text-[10px] text-muted-foreground mb-1 flex items-center gap-1">{label}<InfoTip text={info} /></p>
    <div className="flex items-center gap-1">
      <Input type="number" placeholder={phLo} value={lo} onChange={e => onLo(e.target.value)}
        className="bg-secondary border-border rounded-sm h-7 text-[11px] font-mono" data-testid={testId ? `${testId}-min-input` : undefined} />
      <span className="text-muted-foreground/30 text-xs">–</span>
      <Input type="number" placeholder={phHi} value={hi} onChange={e => onHi(e.target.value)}
        className="bg-secondary border-border rounded-sm h-7 text-[11px] font-mono" data-testid={testId ? `${testId}-max-input` : undefined} />
    </div>
  </div>
);

/**
 * The complete Deal-Explorer filter set (text search, keyword rules, category + range filters).
 * Shared by the Explorer and the KI Deal Screening — same fields, same semantics.
 * `onSearch` (optional) renders the Search button; `searchPlaceholder` overrides the input hint.
 */
export function DealFilterPanel({ filters, updateFilter, setFilters, sectorConfig = {}, ownerOptions = [], onSearch, searchPlaceholder, accent = 'blue' }) {
  const accentBtn = accent === 'violet' ? 'bg-violet-700 hover:bg-violet-600' : 'bg-blue-600 hover:bg-blue-500';
  const categories = useImportCategories();
  const categoryOptions = categoryOptionsFrom(categories);
  return (
    <div className="bg-card border border-border rounded-sm p-4 space-y-4" data-testid="deal-filter-panel">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={filters.keyword} onChange={e => updateFilter('keyword', e.target.value)}
            data-testid="explorer-search-input"
            placeholder={searchPlaceholder || 'Search by name, code name, description, products...'}
            className="pl-10 bg-secondary border-border text-foreground rounded-sm h-10 text-sm"
            onKeyDown={e => e.key === 'Enter' && onSearch && onSearch()} />
        </div>
        {onSearch && (
          <Button onClick={onSearch} className={`${accentBtn} text-white rounded-sm h-10 px-4 text-sm shrink-0`}>
            Search
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-5 gap-y-2" data-testid="deal-filter-toggles">
        <label className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer hover:text-foreground">
          <Checkbox checked={!!filters.exclude_viewed} onCheckedChange={v => updateFilter('exclude_viewed', !!v)}
            data-testid="filter-exclude-viewed" className="w-3.5 h-3.5" />
          Von mir gesehene ausblenden
        </label>
        <label className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer hover:text-foreground">
          <Checkbox checked={!!filters.exclude_pass_proposed} onCheckedChange={v => updateFilter('exclude_pass_proposed', !!v)}
            data-testid="filter-exclude-pass-proposed" className="w-3.5 h-3.5" />
          Pass-Vorschläge ausblenden
        </label>
      </div>

      <div className="flex flex-wrap gap-3 items-start" data-testid="keyword-rules">
        <KeywordChips label="Enthält Keywords" testId="kw-include"
          chips={filters.include_keywords || []}
          onChange={v => updateFilter('include_keywords', v)}
          placeholder="Keyword + Enter (z. B. Solar, Wartung)…"
          color={{ border: 'border-emerald-500/30', chip: 'bg-emerald-500/15 text-emerald-300' }}
          extra={<>
            <InfoTip text="Deals müssen diese Keywords in Name, Code-Name, Beschreibung, Produkten oder Subsektor enthalten — Regel rechts wählen (mind. eines vs. alle)." />
            <select value={filters.include_mode || 'any'} data-testid="kw-include-mode"
              onChange={e => updateFilter('include_mode', e.target.value)}
              className="h-5 text-[10px] rounded-sm border border-border bg-secondary text-foreground px-1">
              <option value="any">Regel: mind. eines (ODER)</option>
              <option value="all">Regel: alle (UND)</option>
            </select>
          </>} />
        <KeywordChips label="Schließt Keywords aus" testId="kw-exclude"
          chips={filters.exclude_keywords || []}
          onChange={v => updateFilter('exclude_keywords', v)}
          placeholder="Keyword + Enter (z. B. Beratung)…"
          color={{ border: 'border-red-500/30', chip: 'bg-red-500/15 text-red-300' }}
          extra={<InfoTip text="Enthält ein Deal eines dieser Keywords (Name, Code-Name, Beschreibung, Produkte, Subsektor), wird er ausgeblendet — Ausschluss gewinnt immer." />} />
      </div>

      <div>
        <p className="text-[9px] text-muted-foreground/50 uppercase tracking-widest font-bold mb-2">Category Filters — tick one or more</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          <MultiSelect testid="sector" label="Sector" info={FIELD_INFO.sector} options={SECTOR_OPTIONS}
            selected={filters.sector} onChange={v => updateFilter('sector', v)} placeholder="All Sectors" />
          <div>
            <p className="text-[10px] text-muted-foreground mb-1 flex items-center gap-1">Subsector<InfoTip text={FIELD_INFO.subsector} /></p>
            <Select value={filters.subsector || 'all'} onValueChange={v => updateFilter('subsector', v === 'all' ? '' : v)}>
              <SelectTrigger data-testid="subsector-filter-trigger" className="bg-secondary border-border rounded-sm h-8 text-[11px]">
                <SelectValue placeholder="All Subsectors" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border rounded-sm max-h-60">
                <SelectItem value="all" className="text-xs">All Subsectors</SelectItem>
                {Object.entries(sectorConfig).map(([sector, subs]) => (
                  <React.Fragment key={sector}>
                    <SelectItem value={`__header_${sector}`} disabled className="text-[9px] text-muted-foreground font-bold">— {sector} —</SelectItem>
                    {(subs || []).sort().map(s => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}
                  </React.Fragment>
                ))}
              </SelectContent>
            </Select>
          </div>
          <MultiSelect testid="recurring" label="Recurring Revenue" info={FIELD_INFO.recurring_revenue} options={RECURRING_OPTIONS}
            selected={filters.recurring_revenue} onChange={v => updateFilter('recurring_revenue', v)} />
          <MultiSelect testid="succession" label="Succession Case" info={FIELD_INFO.succession_case} options={SUCCESSION_OPTIONS}
            selected={filters.succession_case} onChange={v => updateFilter('succession_case', v)} />
          <MultiSelect testid="infra" label="Infra Adjacent" info={FIELD_INFO.infrastructure_adjacent} options={INFRA_OPTIONS}
            selected={filters.infrastructure_adjacent} onChange={v => updateFilter('infrastructure_adjacent', v)} />
          <MultiSelect testid="rating" label="Bewertung" info="Heuristik (auto) = automatisch aus den Profildaten berechnet (ohne KI). KI/Claude = per KI analysiert oder manuell bewertet."
            options={RATING_OPTIONS}
            selected={filters.rating_source || []} onChange={v => updateFilter('rating_source', v)} />
          <MultiSelect testid="source" label="Source" info={FIELD_INFO.deal_type} options={SOURCE_OPTIONS}
            selected={filters.deal_type} onChange={v => updateFilter('deal_type', v)} placeholder="All Sources" />
          <MultiSelect testid="stage" label="Pipeline Stage" info="Current pipeline stage of the deal." options={STAGE_OPTIONS}
            selected={filters.pipeline_stage} onChange={v => updateFilter('pipeline_stage', v)} placeholder="All Stages" />
          {categories.length > 0 && (
            <MultiSelect testid="category" label="Kategorie" info="Import-Kategorie aus dem Batch-Upload (vom Admin benannt, z. B. eine Orbis-Liste oder Region). „Ohne Kategorie“ = ältere Bestände ohne Zuordnung."
              options={categoryOptions} selected={filters.import_category || []} onChange={v => updateFilter('import_category', v)} placeholder="Alle Kategorien" />
          )}
          <MultiSelect testid="owner" label="Claimed by" info="Filter by the team member who claimed the deal." options={ownerOptions}
            selected={filters.owner_id || []} onChange={v => updateFilter('owner_id', v)} placeholder="Anyone" />
        </div>
      </div>

      <div>
        <p className="text-[9px] text-muted-foreground/50 uppercase tracking-widest font-bold mb-2">Range Filters</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <RangeInputs label="Founded Year" info={FIELD_INFO.founded} phLo="From" phHi="To"
            lo={filters.founded_min} hi={filters.founded_max} onLo={v => updateFilter('founded_min', v)} onHi={v => updateFilter('founded_max', v)} />
          <RangeInputs label="Employees" info={FIELD_INFO.employees}
            lo={filters.employees_min} hi={filters.employees_max} onLo={v => updateFilter('employees_min', v)} onHi={v => updateFilter('employees_max', v)} />
          <RangeInputs label="Revenue (€m)" info={FIELD_INFO.revenue} testId="revenue"
            lo={filters.revenue_min} hi={filters.revenue_max} onLo={v => updateFilter('revenue_min', v)} onHi={v => updateFilter('revenue_max', v)} />
          <RangeInputs label="Rev / Empl (€k)" info={FIELD_INFO.rev_per_employee} testId="rpe"
            lo={filters.rpe_min} hi={filters.rpe_max} onLo={v => updateFilter('rpe_min', v)} onHi={v => updateFilter('rpe_max', v)} />
          <RangeInputs label="Owner Age" info={FIELD_INFO.owner_age}
            lo={filters.owner_age_min} hi={filters.owner_age_max} onLo={v => updateFilter('owner_age_min', v)} onHi={v => updateFilter('owner_age_max', v)} />
          <RangeInputs label="Asset-Heavy (KI 0–100)" info="KI-Score, wie anlagenintensiv / asset-heavy das Geschäftsmodell ist (viel Sachanlagevermögen, Netze, Anlagen, Fuhrpark = hoch). Wird beim Fonds-Screening je Treffer von Claude berechnet und am Deal zwischengespeichert — nur bereits gescreente Deals haben einen Wert." testId="asset-heavy"
            lo={filters.asset_heavy_min} hi={filters.asset_heavy_max} onLo={v => updateFilter('asset_heavy_min', v)} onHi={v => updateFilter('asset_heavy_max', v)} />
          <div className="col-span-2">
            <ScoreRange min={filters.succession_score_min} max={filters.succession_score_max}
              onChange={(lo, hi) => setFilters(prev => ({ ...prev, succession_score_min: lo, succession_score_max: hi }))} />
          </div>
        </div>
      </div>
    </div>
  );
}
